import {
  CalendarDays,
  Camera,
  Check,
  Clock3,
  Gamepad2,
  MapPin,
  Menu,
  Minus,
  PackageCheck,
  Phone,
  Plus,
  Search,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Star,
  TicketCheck,
  Trash2,
  Users,
  X,
} from "lucide-react";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  Link,
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import { loadCatalog, money } from "./lib/catalog";
import { useCart } from "./lib/cart";
import { denyMarketing, grantMarketing, marketingChoice, trackMarketing, trackPage } from "./lib/marketing";
import { shippingReviewHandles } from "./lib/shipping-review";
import type { Catalog, Product, ProductVariant } from "./types";

const address = "845 NE 125th St, North Miami, FL 33161";
const phoneDisplay = "(786) 695-0579";
const phoneHref = "tel:+17866950579";
const email = "blessedheadshopmiami@gmail.com";
const mapsUrl = "https://maps.app.goo.gl/V8ZXFydWsTDMPdkB7";
const shopEnabled = import.meta.env.VITE_SHOP_ENABLED === "true";
const papelitoShopEnabled = import.meta.env.VITE_PAPELITO_SHOP_ENABLED === "true";
const brandSlug = (brand: string) => brand.toLowerCase().replace(/\s+/g, "-");
const canBuyProduct = (product: Product) => !shippingReviewHandles.has(product.handle) &&
  (shopEnabled || (papelitoShopEnabled && product.brand === "Papelito"));
const shopperCollections = [
  {
    key: "displays",
    title: "Full boxes & displays",
    description: "Stock up on the products you love, with complete supplier boxes and displays.",
    imageHandle: "elements-elements-green-cone-20pk-29309",
    imageAlt: "Elements cone display",
  },
  {
    key: "unusual",
    title: "Rare & unusual",
    description: "Explore standout designs and specialty RAW finds that are harder to come by.",
    imageHandle: "raw-raw-rolling-tray-ethereal-monster-small-25811",
    imageAlt: "Distinctive RAW Ethereal Monster tray",
  },
  {
    key: "premium",
    title: "Premium accessories",
    description: "Discover statement glass, elevated storage and accessories worth seeking out.",
    imageHandle: "raw-raw-crystal-glass-ashtray-red-27532",
    imageAlt: "RAW crystal glass ashtray",
  },
  {
    key: "brazilian",
    title: "Brazilian products",
    description: "Discover Brazilian rolling papers, tips, filters and carrying cases selected by our North Miami shop.",
    imageHandle: "papelito-pink-king-size",
    imageAlt: "Papelito Pink King Size rolling papers",
  },
] as const;
const categorySlugs: Record<string, string> = {
  "Rolling Papers": "rolling-papers",
  Cones: "cones",
  "Tips & Filters": "tips-filters",
  "Rolling Trays": "rolling-trays",
  "Rollers & Tools": "rollers-tools",
  "Storage & Accessories": "storage-accessories",
  "Glass & Specialty": "glass-specialty",
  "Apparel & Lifestyle": "apparel-lifestyle",
};
const categoryNames = Object.fromEntries(Object.entries(categorySlugs).map(([name, slug]) => [slug, name]));

const productInquiryUrl = (product: Product, variant?: ProductVariant) => {
  const subject = encodeURIComponent(`Product inquiry: ${product.title}`);
  const details = encodeURIComponent(
    `Hi Blessed Headshop Miami,\n\nI would like to request availability and a quote for:\n${product.title}${variant ? `\nOption: ${variant.name}\nSKU: ${variant.sku}` : ""}\n\nThank you.`,
  );
  return `mailto:${email}?subject=${subject}&body=${details}`;
};

const gallery = [
  { src: "/images/venue/hero.png", alt: "Open event floor at Blessed Space" },
  { src: "/images/venue/space-2.jpeg", alt: "Ping-pong table and lounge seating" },
  { src: "/images/venue/space-4.jpeg", alt: "Large projection screen above the lounge" },
  { src: "/images/venue/space-5.jpeg", alt: "Basketball game inside Blessed Space" },
  { src: "/images/venue/space-6.jpeg", alt: "Games and social area at Blessed Space" },
  { src: "/images/venue/space-1.jpeg", alt: "Gaming console at Blessed Space" },
  { src: "/images/venue/space-3.jpeg", alt: "Main Blessed Space game room" },
];

const CatalogContext = createContext<{ catalog: Catalog | null; error: string } | null>(null);

function useCatalog() {
  const value = useContext(CatalogContext);
  if (!value) throw new Error("Catalog context is missing");
  return value;
}

function usePageTitle(title: string) {
  useEffect(() => {
    document.title = `${title} | Blessed Headshop Miami`;
  }, [title]);
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const target = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (target) {
        target.scrollIntoView({ block: "start", behavior: "instant" });
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

function MarketingPageViews() {
  const { pathname, search } = useLocation();
  useEffect(() => { trackPage(pathname + search); }, [pathname, search]);
  return null;
}

function MarketingConsent() {
  const [choice, setChoice] = useState(marketingChoice);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener("blessed-open-marketing-choices", show);
    return () => window.removeEventListener("blessed-open-marketing-choices", show);
  }, []);
  if (choice && !open) return null;
  return <section className="marketing-consent" aria-label="Advertising measurement choices">
    <div><strong>Marketing choices</strong><p>With your permission, we use the Meta Pixel to measure product browsing, cart additions, checkout starts, and event inquiries. The Shopify checkout manages purchase-measurement consent separately. We do not send your form contact details to Meta. <Link to="/policies/privacy">Privacy details</Link></p></div>
    <div className="marketing-consent-actions">
      <button type="button" className="button button-primary" onClick={() => { grantMarketing(); setChoice("granted"); setOpen(false); }}>Allow measurement</button>
      <button type="button" className="button button-ghost" onClick={() => { denyMarketing(); setChoice("denied"); setOpen(false); }}>Decline</button>
    </div>
  </section>;
}

function SeoMetadata() {
  const { pathname } = useLocation();
  const { catalog } = useCatalog();
  useEffect(() => {
    const origin = "https://blessedheadshop.com";
    const path = pathname.replace(/\/$/, "") || "/";
    const product = path.startsWith("/product/") ? catalog?.products.find((item) => `/product/${item.handle}` === path) : undefined;
    const category = path.startsWith("/category/") ? categoryNames[path.split("/")[2]] : undefined;
    const collection = path.startsWith("/collections/") ? shopperCollections.find((item) => item.key === path.split("/")[2]) : undefined;
    const brand = path === "/shop/raw" ? "RAW" : path === "/shop/elements" ? "Elements" : path === "/shop/papelito" ? "Papelito" : path === "/shop/puff-life" ? "PUFF Life" : undefined;
    let title = "RAW, Elements & Specialty Smoking Accessories | Blessed Headshop Miami";
    let description = "Shop RAW, Elements and selected Brazilian brands: papers, tips, filters and PUFF Life carrying cases. Blessed Headshop Miami; adults 21+; North Miami pickup.";
    let canonicalPath = path;
    let indexable = true;
    if (product) {
      title = `${product.title} | ${product.category} | Blessed Headshop Miami`;
      const details = product.description.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 110);
      const summary = `${product.title} — ${details || `Explore this ${product.category.toLowerCase()} item from ${product.brand}.`} ${product.packageType && product.packageType !== "Packaging details available on request" ? product.packageType : "Packaging details available on request"}. Browse at Blessed Headshop Miami; adults 21+.`;
      description = summary.length > 160 ? `${summary.slice(0, 157).replace(/\s+\S*$/, "")}…` : summary;
    } else if (category) {
      title = `${category} | Blessed Headshop Miami`;
      description = `Browse ${category.toLowerCase()} by RAW, Elements and Papelito. See product details, sale units and availability. Adults 21+.`;
    } else if (collection) {
      title = `${collection.title} | Blessed Headshop Miami`;
      description = `${collection.description} See packaging and availability in our online catalog. Adults 21+.`;
    } else if (brand) {
      title = brand === "PUFF Life" ? "PUFF Life EVA Carrying Cases | Blessed Headshop Miami" : brand === "Papelito" ? "Papelito Rolling Papers, Tips & Filters | Blessed Headshop Miami" : `${brand} Rolling Papers, Cones & Accessories | Blessed Headshop Miami`;
      description = brand === "PUFF Life" ? "Shop PUFF Life Mini, Slim, Classic and Pro protective EVA carrying cases. Explore colors, collaborations and sizes in inches. North Miami pickup available." : brand === "Papelito" ? "Shop Papelito rolling papers, tips and filters by the booklet or bag at Blessed Headshop Miami. Adults 21+." : `Explore ${brand} rolling papers, cones and specialty accessories. Full boxes and displays where specified. Adults 21+.`;
    } else if (path === "/blessed-space") {
      title = "Blessed Space Events & Games in North Miami | Blessed Headshop Miami";
      description = "Discover Blessed Space: games, sports, private gatherings and the Blessed Headshop Miami store at 845 NE 125th St, North Miami. View photos, hours and contact details.";
    } else if (path.startsWith("/policies/")) {
      const policy = policyContent[path.split("/")[2]];
      if (policy) { title = `${policy.title} | Blessed Headshop Miami`; description = policy.intro; }
      else indexable = false;
    } else if (path !== "/") {
      indexable = false;
      canonicalPath = "/";
    }
    if (path.startsWith("/product/") && !product && catalog) indexable = false;
    document.title = title;
    const update = (selector: string, tag: "meta" | "link", key: "name" | "property" | "rel", name: string, value: string) => {
      let node = document.head.querySelector<HTMLElement>(selector);
      if (!node) { node = document.createElement(tag); node.setAttribute(key, name); document.head.appendChild(node); }
      node.setAttribute(tag === "link" ? "href" : "content", value);
    };
    update('meta[name="description"]', "meta", "name", "description", description);
    update('meta[name="robots"]', "meta", "name", "robots", indexable ? "index, follow" : "noindex, follow");
    update('link[rel="canonical"]', "link", "rel", "canonical", `${origin}${canonicalPath}`);
    update('meta[property="og:title"]', "meta", "property", "og:title", title);
    update('meta[property="og:description"]', "meta", "property", "og:description", description);
    update('meta[property="og:url"]', "meta", "property", "og:url", `${origin}${canonicalPath}`);
    update('meta[property="og:image"]', "meta", "property", "og:image", product?.images[0] || `${origin}/images/blessed-brand.jpg`);
    document.querySelectorAll<HTMLScriptElement>('script[data-seo-route]').forEach((node) => { if (node.dataset.seoRoute !== path) node.remove(); });
  }, [catalog, pathname]);
  return null;
}

function Header() {
  const [open, setOpen] = useState(false);
  const { itemCount, setDrawerOpen } = useCart();
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    ["PRODUCTS", "/"],
    ["BLESSED SPACE", "/blessed-space"],
  ];
  const productsActive = location.pathname === "/"
    || location.pathname.startsWith("/products")
    || location.pathname.startsWith("/shop")
    || location.pathname.startsWith("/category/")
    || location.pathname.startsWith("/collections/")
    || location.pathname.startsWith("/product/")
    || location.pathname === "/cart";

  return (
    <>
      <div className="notice-bar">Adults 21+ only · Carrier-calculated US shipping · Free North Miami pickup</div>
      <header className="site-header">
        <Link to="/" className="wordmark" aria-label="Blessed Headshop Miami products">
          <img src="/images/blessed-brand.jpg" alt="" />
          <span className="wordmark-name"><strong>BLESSED</strong><small>HEADSHOP MIAMI</small></span>
        </Link>
        <button className="menu-button" type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-label="Open navigation">
          {open ? <X /> : <Menu />}
        </button>
        <nav className={open ? "main-nav open" : "main-nav"} aria-label="Main navigation">
          {links.map(([label, path]) => (
            <NavLink key={path} to={path} end={path === "/"} className={({ isActive }) => (isActive || (path === "/" && productsActive) ? "active" : "")}>
              {label}
            </NavLink>
          ))}
        </nav>
        <button className="cart-button" type="button" onClick={() => setDrawerOpen(true)} aria-label={`Open cart with ${itemCount} items`}>
          <ShoppingBag /> <span>Cart</span><b>{itemCount}</b>
        </button>
      </header>
    </>
  );
}

function Footer() {
  const { catalog } = useCatalog();
  return (
    <footer className="site-footer">
      <div className="footer-grid page-width">
        <div>
          <div className="footer-mark">BLESSED</div>
          <p>Selected original brands, full boxes and displays, a welcoming North Miami shop, and a space made for good times.</p>
          <div className="social-row">
            <a href="https://www.instagram.com/blessed.florida?stkn=eWdsaGt6amV1cjg0" target="_blank" rel="noreferrer" aria-label="Instagram @blessed.florida"><Camera /></a>
            <a href={mapsUrl} target="_blank" rel="noreferrer" aria-label="Google Maps"><MapPin /></a>
          </div>
        </div>
        <div><h3>Products</h3><Link to="/">All products</Link><Link to="/shop/raw">RAW</Link><Link to="/shop/elements">Elements</Link><Link to="/shop/papelito">Papelito</Link>{catalog?.products.some((p) => p.brand === "PUFF Life") && <Link to="/shop/puff-life">PUFF Life</Link>}<Link to="/cart">Cart</Link></div>
        <div><h3>Blessed Space</h3><Link to="/blessed-space">About the space</Link><Link to="/blessed-space#gallery">Gallery</Link><Link to="/blessed-space#visit">Visit us</Link></div>
        <div><h3>Policies</h3><Link to="/policies/shipping">Shipping</Link><Link to="/policies/returns">Returns</Link><Link to="/policies/privacy">Privacy</Link><Link to="/policies/terms">Terms & age policy</Link><button className="privacy-choice-link" type="button" onClick={() => window.dispatchEvent(new Event("blessed-open-marketing-choices"))}>Marketing choices</button></div>
      </div>
      <div className="footer-bottom page-width">
        <span>© {new Date().getFullYear()} Blessed Headshop Miami</span>
        <span>For adults 21+. Smoking accessories only. No tobacco or nicotine included.</span>
      </div>
    </footer>
  );
}

function AgeGate() {
  const [status, setStatus] = useState<"checking" | "open" | "denied" | "accepted">("checking");
  const enterRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const accepted = sessionStorage.getItem("blessed-age-verified") === "21+";
    setStatus(accepted ? "accepted" : "open");
  }, []);
  useEffect(() => {
    if (status === "open") enterRef.current?.focus();
  }, [status]);
  if (status === "checking" || status === "accepted") return null;

  const accept = () => {
    sessionStorage.setItem("blessed-age-verified", "21+");
    trackPage(window.location.pathname + window.location.search);
    setStatus("accepted");
  };

  return (
    <div className="age-backdrop" role="presentation">
      <section className="age-modal" role="dialog" aria-modal="true" aria-labelledby="age-title">
        <div className="age-seal">21+</div>
        {status === "denied" ? (
          <>
            <h2 id="age-title">This site is for adults only.</h2>
            <p>You must be 21 or older to visit Blessed Headshop Miami online.</p>
          </>
        ) : (
          <>
            <span className="eyebrow">Welcome to Blessed</span>
            <h2 id="age-title">Are you 21 or older?</h2>
            <p>By entering, you confirm that you are of legal age in your jurisdiction.</p>
            <div className="age-actions">
              <button ref={enterRef} className="button button-primary" type="button" onClick={accept}>Yes, enter site</button>
              <button className="button button-ghost" type="button" onClick={() => setStatus("denied")}>No, I’m under 21</button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function Hero() {
  return (
    <section className="hero page-width">
      <div className="hero-copy">
        <span className="eyebrow">North Miami’s neighborhood headshop</span>
        <h1>Good products.<br /><em>Better energy.</em></h1>
        <p>Shop RAW, Elements and selected original brands by the box or display, pick up locally, and discover the one-of-a-kind social space behind the store.</p>
        <div className="hero-actions">
          <Link className="button button-primary" to="/shop">Shop the collection</Link>
          <Link className="button button-outline" to="/venue">Explore Blessed Space</Link>
        </div>
        <div className="hero-rating"><Star fill="currentColor" /> <strong>4.9</strong><span>from 250+ Google reviews</span></div>
      </div>
      <div className="hero-visual">
        <img className="hero-room" src="/images/venue/space-6.jpeg" alt="The social and gaming space inside Blessed Headshop Miami" />
        <div className="hero-logo-card"><img src="/images/blessed-brand.jpg" alt="Blessed Headshop lion logo" /></div>
        <div className="hero-badge"><span>North Miami</span><strong>LOCAL<br />FAVORITE</strong></div>
      </div>
    </section>
  );
}

function Home() {
  usePageTitle("Home");
  const { catalog } = useCatalog();
  const featured = catalog?.products.slice(0, 4) ?? [];
  return (
    <main>
      <Hero />
      <section className="trust-strip">
        <div><ShieldCheck /><span><strong>Original brands</strong>RAW, Elements & selected favorites</span></div>
        <div><PackageCheck /><span><strong>Local pickup</strong>Free in North Miami</span></div>
        <div><TicketCheck /><span><strong>Simple checkout</strong>Securely powered by Shopify</span></div>
        <div><Clock3 /><span><strong>Open late</strong>Until 10 PM Mon–Sat</span></div>
      </section>

      <section className="brand-shop page-width section-space">
        <div className="section-heading"><span className="eyebrow">Featured brands. One trusted shop.</span><h2>Choose your paper</h2></div>
        <div className="brand-panels">
          <Link to="/shop/raw" className="brand-panel raw-panel"><span className="brand-count">RAW collection</span><strong>RAW</strong><p>Natural papers, cones, tips, trays and the culture around them.</p><span className="text-link">Shop RAW</span></Link>
          <Link to="/shop/elements" className="brand-panel elements-panel"><span className="brand-count">Elements collection</span><strong>ELEMENTS</strong><p>Ultra-thin rice papers, cones and tools made for a clean, slow burn.</p><span className="text-link">Shop Elements</span></Link>
        </div>
      </section>

      {featured.length > 0 && (
        <section className="page-width section-space">
          <div className="section-heading split"><div><span className="eyebrow">Fresh from the catalog</span><h2>Featured essentials</h2></div><Link className="text-link" to="/shop">View all products</Link></div>
          <div className="product-grid compact">{featured.map((product) => <ProductCard key={product.handle} product={product} />)}</div>
        </section>
      )}

      <section className="space-preview section-space">
        <div className="page-width space-grid">
          <div className="space-collage"><img src="/images/venue/hero.png" alt="Open floor at Blessed Space" /><img src="/images/venue/space-5.jpeg" alt="Basketball game at Blessed Space" /></div>
          <div className="space-copy"><span className="eyebrow light">The space behind the shop</span><h2>More room for<br />more good times.</h2><p>750+ square feet for private events, birthdays, game nights, live sports and laid-back gatherings of up to 50 guests.</p><div className="feature-pills"><span><Gamepad2 /> Arcade & games</span><span><Users /> Up to 50 guests</span><span><Sparkles /> Flexible setup</span></div><Link className="button button-light" to="/venue">Plan your event</Link></div>
        </div>
      </section>

      <section className="reviews page-width section-space">
        <div className="section-heading"><span className="eyebrow">Loved around the neighborhood</span><h2>People feel at home here.</h2></div>
        <div className="review-grid">
          <blockquote><div className="stars">★★★★★</div><p>“A huge variety of products, and the staff is always friendly and welcoming.”</p><cite>Google customer</cite></blockquote>
          <blockquote><div className="stars">★★★★★</div><p>“The best inventory and prices around North Miami. Worth the trip.”</p><cite>Local Guide</cite></blockquote>
          <blockquote><div className="stars">★★★★★</div><p>“I love watching soccer in the hangout area in the back.”</p><cite>Google customer</cite></blockquote>
        </div>
      </section>

      <VisitSection />
    </main>
  );
}

function ProductCard({ product }: { product: Product }) {
  const variant = product.variants[0];
  const purchasable = Boolean(canBuyProduct(product) && variant?.price !== null && variant?.shopifyVariantId);
  const { add } = useCart();
  return (
    <article className="product-card">
      <Link to={`/product/${product.handle}`} className="product-image-wrap">
        <span className={`brand-tag ${brandSlug(product.brand)}`}>{product.brand}</span>
        {product.images[0] ? <img src={product.images[0]} alt={product.title} loading="lazy" /> : <div className="image-placeholder"><ShoppingBag /></div>}
      </Link>
      <div className="product-card-body">
        <span className="product-category">{product.category}</span>
        <h3><Link to={`/product/${product.handle}`}>{product.title}</Link></h3>
        {product.packageType && product.packageType !== "Packaging details available on request" && <p className="product-pack">{product.packageType}</p>}
        <div className="product-card-bottom">
          <span className={purchasable ? "product-price" : "product-price pending"}>{money(variant?.price ?? null)}</span>
          <button type="button" className="quick-add" disabled={!purchasable} onClick={() => variant && add(product, variant)} aria-label={`Add ${product.title} to cart`}>
            {purchasable ? <Plus /> : <Clock3 />}
          </button>
        </div>
      </div>
    </article>
  );
}

function Shop({ brand, selectedCategory, collectionKey }: { brand?: "RAW" | "ELEMENTS" | "Papelito" | "PUFF Life"; selectedCategory?: string; collectionKey?: string }) {
  usePageTitle(selectedCategory ? `${selectedCategory} Products` : brand ? `${brand} Products` : "Products");
  const { catalog, error } = useCatalog();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState(selectedCategory || "All products");
  const [sort, setSort] = useState("featured");
  const [limit, setLimit] = useState(24);
  useEffect(() => { setCategory(selectedCategory || "All products"); setLimit(24); }, [brand, selectedCategory, collectionKey]);

  const available = useMemo(() => (catalog?.products ?? []).filter((product) => !brand || product.brand === brand), [brand, catalog]);
  const activeCollection = shopperCollections.find((item) => item.key === collectionKey);
  const shopIntro = selectedCategory
    ? `Explore ${selectedCategory.toLowerCase()} from RAW, Elements, Papelito, PUFF Life and selected original brands. Browse product details, sale units and availability from Blessed Headshop Miami.`
    : activeCollection ? activeCollection.description
    : brand === "PUFF Life" ? "Protective EVA carrying cases in Mini, Slim, Classic and Pro sizes. Explore Brazilian designs, colors and collaborations, sold one case at a time. Accessories shown in photos are not included."
    : brand === "RAW" ? "Natural papers, cones, tips and standout accessories from the original rolling paper culture."
    : brand === "ELEMENTS" ? "Rice papers, cones and tools engineered for an ultra-clean burn."
    : brand === "Papelito" ? "Explore Papelito papers, tips and filters carried by our North Miami shop. Each listing states the individual booklet or bag included."
    : "A destination for hard-to-find smoking accessories, full boxes and displays, and specialty collectibles. Explore RAW, Elements and selected Brazilian brands at Blessed Headshop Miami.";
  const categories = useMemo(() => ["All products", ...Array.from(new Set(available.map((product) => product.category))).sort()], [available]);
  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const values = available.filter((product) =>
      (!activeCollection || product.collections.includes(activeCollection.title)) &&
      (category === "All products" || product.category === category) &&
      (!normalized || `${product.title} ${product.description} ${product.category}`.toLowerCase().includes(normalized)),
    );
    if (sort === "az") values.sort((a, b) => a.title.localeCompare(b.title));
    if (sort === "za") values.sort((a, b) => b.title.localeCompare(a.title));
    if (sort === "brand") values.sort((a, b) => a.brand.localeCompare(b.brand) || a.title.localeCompare(b.title));
    return values;
  }, [activeCollection, available, category, query, sort]);

  return (
    <main className="shop-page page-width">
      <header className="shop-header">
        <div><span className="eyebrow">Products · Blessed Headshop Miami</span><h1>{selectedCategory ? `${selectedCategory} Products` : activeCollection ? activeCollection.title : brand ? `${brand} Products` : "Not another smoke shop."}</h1><p>{shopIntro}</p></div>
        <div className="shop-identity"><img src="/images/blessed-brand.jpg" alt="Blessed Headshop lion logo" /><div className="catalog-state"><span>{available.length}</span><small>products available</small></div></div>
      </header>

      {!brand && <section className="shopper-intents" aria-labelledby="shopper-intents-title">
        <div className="intent-heading"><span className="eyebrow">Find your kind of find</span><h2 id="shopper-intents-title">Shop your way.</h2><p>Whether you are stocking up or looking for something extraordinary, start with what brought you here.</p></div>
        <div className="intent-grid">{shopperCollections.map((item, index) => {
          const product = catalog?.products.find((candidate) => candidate.handle === item.imageHandle);
          const count = catalog?.products.filter((candidate) => candidate.collections.includes(item.title)).length ?? 0;
          return <Link key={item.key} to={`/collections/${item.key}#catalog-results`} className={`intent-card ${activeCollection?.key === item.key ? "active" : ""}`}>
            <span className="intent-visual">{product?.images[0] && <img src={product.images[0]} alt={item.imageAlt} loading="lazy" />}</span>
            <span className="intent-copy"><small>0{index + 1} / {count} finds</small><strong>{item.title}</strong><span>{item.description}</span><b>Explore collection <span aria-hidden="true">↗</span></b></span>
          </Link>;
        })}</div>
      </section>}

      {(!shopEnabled || catalog?.status !== "checkout-live") && <div className="catalog-notice"><Clock3 /><div><strong>Online catalog available now</strong><span>{papelitoShopEnabled ? "Papelito items can be purchased online. Sunshine boxes and displays remain available to browse while their checkout is being prepared." : "Browse Sunshine boxes and displays or Papelito items carried by the shop. Secure online checkout is being prepared."}</span></div></div>}

      <div id="catalog-results" className="shop-controls">
        <label className="search-field"><Search /><input value={query} onChange={(event) => { setQuery(event.target.value); setLimit(24); }} placeholder="Search products" aria-label="Search products" /></label>
        <select value={sort} onChange={(event) => setSort(event.target.value)} aria-label="Sort products"><option value="featured">Featured</option><option value="az">Name: A–Z</option><option value="za">Name: Z–A</option><option value="brand">Brand</option></select>
      </div>
      {activeCollection && <div className="collection-active"><span>Showing: <strong>{activeCollection.title}</strong></span><Link to="/">View all products <X size={16} /></Link></div>}
      {!brand && <nav className="category-chips" aria-label="Shop by brand"><Link to="/shop/raw">RAW</Link><Link to="/shop/elements">Elements</Link><Link to="/shop/papelito">Papelito</Link>{catalog?.products.some((p) => p.brand === "PUFF Life") && <Link to="/shop/puff-life">PUFF Life</Link>}</nav>}
      <nav className="category-chips" aria-label="Product categories">{categories.map((name) => <Link key={name} to={name === "All products" ? "/" : `/category/${categorySlugs[name]}`} className={category === name ? "active" : ""}>{name}</Link>)}</nav>

      {!catalog && !error && <div className="loading-grid" aria-label="Loading catalog">{Array.from({ length: 8 }, (_, index) => <div className="skeleton" key={index} />)}</div>}
      {error && <div className="empty-state"><h2>We couldn’t load the catalog.</h2><p>{error}</p></div>}
      {catalog && filtered.length === 0 && <div className="empty-state"><Search /><h2>No matches found.</h2><p>Try a broader search or a different category.</p></div>}
      <div className="product-grid">{filtered.slice(0, selectedCategory || brand || collectionKey ? filtered.length : limit).map((product) => <ProductCard key={product.handle} product={product} />)}</div>
      {!selectedCategory && !brand && !collectionKey && filtered.length > limit && <div className="load-more"><button className="button button-outline" type="button" onClick={() => setLimit((value) => value + 24)}>Load more products</button><span>Showing {Math.min(limit, filtered.length)} of {filtered.length}</span></div>}
    </main>
  );
}

function ShopCategory() {
  const { slug } = useParams();
  const selectedCategory = categoryNames[slug || ""];
  return selectedCategory ? <Shop selectedCategory={selectedCategory} /> : <NotFound />;
}

function ShopCollection() {
  const { key } = useParams();
  return shopperCollections.some((item) => item.key === key) ? <Shop collectionKey={key} /> : <NotFound />;
}

function ProductDetail() {
  const { handle } = useParams();
  const { catalog, error } = useCatalog();
  const product = catalog?.products.find((item) => item.handle === handle);
  const [variantIndex, setVariantIndex] = useState(0);
  const [imageIndex, setImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const { add } = useCart();
  usePageTitle(product?.title ?? "Product");
  if (error) return <main className="page-width detail-page"><div className="empty-state"><p>{error}</p></div></main>;
  if (!catalog) return <main className="page-width detail-page"><div className="skeleton detail-skeleton" /></main>;
  if (!product) return <Navigate to="/" replace />;
  const variant = product.variants[variantIndex] ?? product.variants[0];
  const purchasable = Boolean(canBuyProduct(product) && variant?.price !== null && variant?.shopifyVariantId);
  const inquiryUrl = productInquiryUrl(product, variant);
  return (
    <main className="detail-page page-width">
      <div className="breadcrumbs"><Link to="/">Products</Link><span>/</span><Link to={`/shop/${brandSlug(product.brand)}`}>{product.brand}</Link><span>/</span><Link to={`/category/${categorySlugs[product.category]}`}>{product.category}</Link><span>/</span><span>{product.title}</span></div>
      <div className="detail-grid">
        <div className="detail-gallery">
          <div className="detail-main-image"><img src={product.images[imageIndex] || product.images[0]} alt={product.title} /></div>
          {product.images.length > 1 && <div className="detail-thumbs">{product.images.map((image, index) => <button type="button" className={imageIndex === index ? "active" : ""} onClick={() => setImageIndex(index)} key={image}><img src={image} alt={`${product.title} view ${index + 1}`} /></button>)}</div>}
        </div>
        <div className="detail-copy">
          <span className={`brand-tag static ${brandSlug(product.brand)}`}>{product.brand}</span>
          <h1>{product.title}</h1>
          <span className="detail-category">{product.category}</span>
          <p className="detail-price">{money(variant?.price ?? null)}</p>
          <p className="detail-description">{product.description || `Authentic ${product.brand} product, sourced for Blessed Headshop Miami.`}</p>
          {product.variants.length > 1 && <label className="variant-select">Choose an option<select value={variantIndex} onChange={(event) => setVariantIndex(Number(event.target.value))}>{product.variants.map((item, index) => <option key={item.sku} value={index}>{item.name}</option>)}</select></label>}
          <div className="purchase-row"><div className="quantity-control"><button type="button" onClick={() => setQuantity((value) => Math.max(1, value - 1))} aria-label="Decrease quantity"><Minus /></button><span>{quantity}</span><button type="button" aria-label="Increase quantity" onClick={() => setQuantity((value) => Math.min(99, value + 1))}><Plus /></button></div>{purchasable ? <button type="button" className="button button-primary grow" onClick={() => { if (variant) add(product, variant, quantity); }}>Add to cart</button> : <a className="button button-primary grow" href={inquiryUrl}>{variant?.price === null ? "Request a quote" : "Request availability"}</a>}</div>
          <div className="special-order"><PackageCheck /><div><strong>{product.packageType || "Packaging details available on request"}</strong><span>{product.specialOrder ? `Estimated delivery in ${product.deliveryEstimate}, subject to availability. Free North Miami pickup is available.` : "Carried by our North Miami shop. Shipping options and free pickup are available at checkout."}</span></div></div>
          {Object.keys(product.specifications).length > 0 && <div className="specifications"><h2>Product details</h2>{Object.entries(product.specifications).map(([key, value]) => <div key={key}><span>{key}</span><strong>{value}</strong></div>)}</div>}
        </div>
      </div>
    </main>
  );
}

function CartContents({ full = false }: { full?: boolean }) {
  const { items, subtotal, update, remove, clear, setDrawerOpen } = useCart();
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const checkoutEnabled = shopEnabled || (papelitoShopEnabled && items.length > 0 && items.every((item) => item.brand === "Papelito"));

  const checkout = async () => {
    setError("");
    if (!checkoutEnabled) { setError("Secure online checkout is being prepared for these products."); return; }
    if (!ageConfirmed) { setError("Please confirm that you are 21 or older."); return; }
    setBusy(true);
    try {
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ageConfirmed, lines: items.map((item) => ({ merchandiseId: item.shopifyVariantId, quantity: item.quantity })) }),
      });
      const result = await response.json() as { checkoutUrl?: string; error?: string };
      if (!response.ok || !result.checkoutUrl) throw new Error(result.error || "Checkout is unavailable.");
      trackMarketing("InitiateCheckout", { value: Number(subtotal.toFixed(2)), currency: "USD", content_ids: items.map((item) => item.variantSku), contents: items.map((item) => ({ id: item.variantSku, quantity: item.quantity })) });
      window.location.assign(result.checkoutUrl);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Checkout is unavailable.");
    } finally { setBusy(false); }
  };

  if (!items.length) return <div className="cart-empty"><ShoppingBag /><h2>Your cart is empty.</h2><p>Explore Papelito, RAW, Elements and selected original brands.</p><Link className="button button-primary" to="/" onClick={() => setDrawerOpen(false)}>Shop products</Link></div>;
  return (
    <div className={full ? "cart-content full" : "cart-content"}>
      <div className="cart-lines">{items.map((item) => <article className="cart-line" key={item.variantSku}>{item.image && <img src={item.image} alt="" />}<div className="cart-line-copy"><span>{item.brand}</span><h3>{item.title}</h3>{item.variantName !== "1 PC" && item.variantName !== "Default Title" && <small>{item.variantName}</small>}<strong>{money(item.price)}</strong></div><div className="cart-line-actions"><div className="quantity-control small"><button type="button" onClick={() => update(item.variantSku, item.quantity - 1)} aria-label="Decrease quantity"><Minus /></button><span>{item.quantity}</span><button type="button" onClick={() => update(item.variantSku, item.quantity + 1)} aria-label="Increase quantity"><Plus /></button></div><button className="remove-button" type="button" onClick={() => remove(item.variantSku)} aria-label={`Remove ${item.title}`}><Trash2 /></button></div></article>)}</div>
      <div className="cart-summary"><div className="summary-row"><span>Subtotal</span><strong>{money(subtotal)}</strong></div><div className="summary-row"><span>Shipping</span><span>Carrier-calculated at checkout</span></div><p>Shipping is available to 49 U.S. states (including Alaska, excluding Hawaii) and Washington, DC, where permitted. We do not ship to Puerto Rico or other U.S. territories. Free pickup is available at our North Miami store.</p><label className="age-check"><input type="checkbox" checked={ageConfirmed} onChange={(event) => setAgeConfirmed(event.target.checked)} /><span>I confirm that I am 21 years of age or older.</span></label>{error && <div className="form-error" role="alert">{error}</div>}<button className="button button-primary checkout-button" type="button" disabled={busy || !checkoutEnabled} onClick={checkout}>{checkoutEnabled ? (busy ? "Opening secure checkout…" : "Checkout securely with Shopify") : "Secure checkout coming soon"}</button><button className="clear-cart" type="button" onClick={clear}>Clear cart</button><div className="secure-note"><ShieldCheck /> Secure checkout · Shop Pay · Visa · Mastercard · Amex</div></div>
    </div>
  );
}

function CartDrawer() {
  const { drawerOpen, setDrawerOpen, itemCount } = useCart();
  useEffect(() => {
    document.body.classList.toggle("no-scroll", drawerOpen);
    return () => document.body.classList.remove("no-scroll");
  }, [drawerOpen]);
  return (
    <><button className={drawerOpen ? "drawer-overlay open" : "drawer-overlay"} aria-label="Close cart" type="button" onClick={() => setDrawerOpen(false)} /><aside className={drawerOpen ? "cart-drawer open" : "cart-drawer"} aria-hidden={!drawerOpen}><div className="drawer-heading"><div><span>Your cart</span><b>{itemCount} items</b></div><button type="button" onClick={() => setDrawerOpen(false)} aria-label="Close cart"><X /></button></div><CartContents /></aside></>
  );
}

function CartPage() {
  usePageTitle("Cart");
  return <main className="cart-page page-width"><span className="eyebrow">Almost there</span><h1>Your cart</h1><CartContents full /></main>;
}

function VenueForm() {
  const [state, setState] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = Object.fromEntries(new FormData(form).entries());
    if (String(data.companyWebsite || "").trim()) return;
    setState("sending"); setMessage("");
    try {
      const response = await fetch(`https://formsubmit.co/ajax/${email}`, {
        method: "POST",
        headers: { accept: "application/json", "content-type": "application/json" },
        body: JSON.stringify({
          ...data,
          _subject: `Blessed Space inquiry — ${data.eventType} on ${data.date}`,
          _replyto: data.email,
          _template: "table",
          _captcha: "false",
          _honey: data.companyWebsite,
        }),
      });
      const result = await response.json() as { success?: boolean | string };
      if (!response.ok || (result.success !== true && result.success !== "true")) throw new Error("Delivery was not confirmed.");
      trackMarketing("Lead", { content_name: "Blessed Space event inquiry", content_category: "Venue" });
      setState("success"); setMessage("Thanks! We’ll be in touch shortly."); form.reset();
    } catch {
      const subject = encodeURIComponent(`Blessed Space inquiry — ${data.eventType} on ${data.date}`);
      const body = encodeURIComponent(Object.entries(data).filter(([key]) => key !== "companyWebsite").map(([key, value]) => `${key}: ${value || "—"}`).join("\n"));
      window.location.assign(`mailto:${email}?subject=${subject}&body=${body}`);
      setState("error");
      setMessage("Automatic delivery could not be confirmed. Please send the email draft to complete your request, or call us.");
    }
  };
  return (
    <form className="venue-form" onSubmit={submit}>
      <div className="form-heading"><span className="eyebrow">Tell us about your event</span><h2>Request a date</h2><p>We’ll confirm availability and help shape the right setup.</p></div>
      <div className="form-grid"><label>Full name<input name="name" autoComplete="name" required /></label><label>Email<input name="email" type="email" autoComplete="email" required /></label><label>Phone<input name="phone" type="tel" autoComplete="tel" /></label><label>Preferred date<input name="date" type="date" required /></label><label>Event type<select name="eventType" required defaultValue=""><option value="" disabled>Select one</option><option>Birthday party</option><option>Private event</option><option>Business event</option><option>Watch party</option><option>Live or virtual event</option><option>Other</option></select></label><label>Expected guests<input name="guests" type="number" min="1" max="50" /></label></div>
      <label>Anything else we should know?<textarea name="message" rows={4} /></label><label className="honeypot" aria-hidden="true">Website<input name="companyWebsite" tabIndex={-1} autoComplete="off" /></label>
      <button className="button button-primary" type="submit" disabled={state === "sending"}>{state === "sending" ? "Sending…" : "Send event request"}</button>
      {message && <div className={state === "success" ? "form-success" : "form-error"} role="status">{message}</div>}
      <p className="form-fallback">Prefer to talk? Call <a href={phoneHref}>{phoneDisplay}</a>.</p>
    </form>
  );
}

function SpaceGallery() {
  const [selected, setSelected] = useState<(typeof gallery)[number] | null>(null);
  return (
    <section id="gallery" className="gallery-page page-width space-gallery">
      <header className="page-intro"><span className="eyebrow">Inside Blessed Space</span><h2>A space with its own energy.</h2><p>Games, screens, open floor space and comfortable corners for your people. Select any photo to see it larger.</p></header>
      <div className="gallery-grid">{gallery.map((item, index) => <button className={`gallery-item gallery-${index + 1}`} type="button" key={item.src} onClick={() => setSelected(item)}><img src={item.src} alt={item.alt} loading="lazy" /><span>View photo</span></button>)}</div>
      {selected && <div className="lightbox" role="dialog" aria-modal="true" aria-label="Photo viewer"><button type="button" onClick={() => setSelected(null)} aria-label="Close photo"><X /></button><img src={selected.src} alt={selected.alt} /><p>{selected.alt}</p></div>}
    </section>
  );
}

function BlessedSpace() {
  usePageTitle("Blessed Space");
  return (
    <main>
      <section className="venue-hero"><img src="/images/venue/hero.png" alt="Blessed Space event floor" /><div className="venue-hero-copy page-width"><span className="eyebrow light">The experience behind Blessed Headshop Miami</span><h1>Shop.<br />Play.<br /><em>Celebrate.</em></h1><p>Blessed Space brings together a neighborhood headshop, games, live sports and a flexible private event space in North Miami.</p><div className="space-hero-actions"><a href="#inquiry" className="button button-light">Request your date</a><Link to="/" className="button button-space-outline">Browse products</Link></div><div className="space-rating"><Star fill="currentColor" /><strong>4.9</strong><span>from 250+ Google reviews</span></div></div><div className="space-brand-mark"><img src="/images/blessed-brand.jpg" alt="Blessed Headshop lion logo" /></div></section>
      <section className="venue-stats page-width"><div><strong>750+</strong><span>square feet</span></div><div><strong>50</strong><span>guest capacity</span></div><div><strong>$200</strong><span>packages from</span></div><div><strong>1</strong><span>unforgettable night</span></div></section>
      <section className="trust-strip space-trust">
        <div><ShieldCheck /><span><strong>Neighborhood favorite</strong>Welcoming North Miami shop</span></div>
        <div><Gamepad2 /><span><strong>Games included</strong>Arcade, ping-pong & consoles</span></div>
        <div><TicketCheck /><span><strong>Private events</strong>Birthdays, watch parties & more</span></div>
        <div><Clock3 /><span><strong>Open late</strong>Until 10 PM Mon-Sat</span></div>
      </section>
      <section className="venue-story page-width section-space"><div><span className="eyebrow">More than a store</span><h2>A place to stay, play and connect.</h2><p>Stop in for the products, stay for the atmosphere. Blessed Space feels social and ready to enjoy from the moment your group walks in.</p></div><div className="venue-features"><article><Gamepad2 /><h3>Games included</h3><p>Ping-pong, arcade play, basketball challenges and console gaming.</p></article><article><Sparkles /><h3>Flexible events</h3><p>Open floor space that adapts to birthdays, watch parties and private gatherings.</p></article><article><Users /><h3>Room to connect</h3><p>Comfortable seating, social areas and capacity for up to 50 guests.</p></article><article><CalendarDays /><h3>Event inquiries</h3><p>Tell us your preferred date and we’ll confirm availability and the best setup for your event.</p></article></div></section>
      <SpaceGallery />
      <section className="reviews page-width section-space">
        <div className="section-heading"><span className="eyebrow">Loved around the neighborhood</span><h2>People feel at home here.</h2></div>
        <div className="review-grid">
          <blockquote><div className="stars">★★★★★</div><p>“A huge variety of products, and the staff is always friendly and welcoming.”</p><cite>Google customer</cite></blockquote>
          <blockquote><div className="stars">★★★★★</div><p>“The best inventory and prices around North Miami. Worth the trip.”</p><cite>Local Guide</cite></blockquote>
          <blockquote><div className="stars">★★★★★</div><p>“I love watching soccer in the hangout area in the back.”</p><cite>Google customer</cite></blockquote>
        </div>
      </section>
      <section id="inquiry" className="inquiry-section page-width section-space"><div className="inquiry-copy"><span className="eyebrow">Make it yours</span><h2>Ready when your group is.</h2><p>Tell us your preferred date and event details. We’ll confirm availability and help plan your visit.</p><div className="inquiry-contact"><Phone /><div><small>Call the venue</small><a href={phoneHref}>{phoneDisplay}</a></div></div></div><VenueForm /></section>
      <VisitSection />
    </main>
  );
}

function Gallery() {
  return <Navigate to="/blessed-space#gallery" replace />;
}

function VisitSection() {
  return (
    <section id="visit" className="visit-section"><div className="page-width visit-grid"><div className="visit-card"><span className="eyebrow">Visit us</span><h2>Your neighborhood stop in North Miami.</h2><p><MapPin />{address}</p><p><Phone /><a href={phoneHref}>{phoneDisplay}</a></p><div className="hours"><div><span>Monday-Saturday</span><strong>9:30 AM-10:00 PM</strong></div><div><span>Sunday</span><strong>10:00 AM-8:00 PM</strong></div></div><a className="button button-primary" href={mapsUrl} target="_blank" rel="noreferrer">Get directions</a></div><div className="visit-image"><img src="/images/venue/space-3.jpeg" alt="Inside Blessed Headshop Miami" /><span>North Miami · Florida</span></div></div></section>
  );
}

function Contact() {
  usePageTitle("Contact");
  return <main><section className="contact-hero page-width"><span className="eyebrow">Questions, pickup or events</span><h1>Come through.<br />We’re here late.</h1><p>Reach the shop directly or stop by on NE 125th Street.</p></section><section className="contact-cards page-width"><a href={phoneHref}><Phone /><span>Call us</span><strong>{phoneDisplay}</strong></a><a href={`mailto:${email}`}><Sparkles /><span>Email us</span><strong>{email}</strong></a><a href={mapsUrl} target="_blank" rel="noreferrer"><MapPin /><span>Find us</span><strong>{address}</strong></a></section><VisitSection /></main>;
}

const policyContent: Record<string, { title: string; intro: string; sections: Array<[string, string]> }> = {
  shipping: { title: "Shipping & pickup", intro: "Carrier-calculated delivery to 49 U.S. states and Washington, DC, plus free North Miami pickup. Hawaii is excluded.", sections: [["Shipping rates", "Shipping rates are calculated by the carrier at checkout based on the destination, package size and selected service."], ["Where we ship", "We ship to 49 U.S. states, including Alaska but excluding Hawaii, and Washington, DC, where the products and delivery service are permitted by applicable law. We do not ship to Puerto Rico or other U.S. territories."], ["Availability", "Papelito and PUFF Life items are carried by our North Miami shop. Sunshine items are fulfilled as special orders, subject to supplier availability."], ["Local pickup", `Pickup is free at ${address}. We’ll contact you when your order is ready.`]] },
  returns: { title: "Returns", intro: "We accept eligible returns within 14 days of delivery.", sections: [["Return condition", "Products must be unused, unopened, in original packaging and in resalable condition."], ["Return shipping", "Customers are responsible for return shipping unless the item was incorrect or arrived damaged."], ["Non-returnable items", "Opened smoking accessories, hygiene-sensitive products, clearance items and gift cards are final sale unless required by law."], ["Start a return", `Email ${email} with your order number and reason for return before sending anything back.`]] },
  privacy: { title: "Privacy", intro: "We use information to serve you, complete orders and, with your permission, measure advertising.", sections: [["Information we collect", "Contact, shipping, order and payment-related information is collected when you shop or send an inquiry."], ["Payments", "Payment details are handled by Shopify and its payment providers. Blessed Headshop Miami does not store complete card numbers."], ["How information is used", "We use information to process orders, provide support, prevent fraud and respond to venue inquiries. Blessed Space inquiries are processed by FormSubmit for email delivery."], ["Advertising measurement", "With applicable permission, the Meta Pixel (ID 1577380033694621) measures page views, add-to-cart actions, checkout starts and successful Blessed Space inquiries on this site. Shopify checkout separately measures completed purchases, including the final order value and currency, when its marketing and analytics permissions allow it. Product identifiers and values may be shared with Meta; your inquiry form name, email, phone and message are not sent through our pixel. You may decline or change this site's choice using Marketing choices in the footer; Shopify manages checkout privacy choices separately."], ["Your choices", `To ask about your personal information, email ${email}.`]] },
  terms: { title: "Terms & age policy", intro: "Use of this site is limited to adults 21 years of age or older.", sections: [["Age requirement", "By entering this site or placing an order, you confirm that you are at least 21 and legally allowed to purchase these products where you live."], ["Product use", "Products are sold as legal smoking accessories. No tobacco, nicotine or controlled substance is included."], ["Availability", "Papelito and PUFF Life items are carried by our North Miami shop; availability may change before fulfillment. Sunshine special orders are subject to supplier availability and estimated at 5-10 business days. If an ordered item cannot be fulfilled, we’ll contact you and issue the appropriate refund."], ["Sales tax", "Sales tax is collected where required. Blessed Headshop Miami is currently registered in Florida."], ["Responsible purchasing", "Customers are responsible for knowing and following the laws that apply in their jurisdiction."]]} ,
};

function Policy() {
  const { slug } = useParams();
  const content = policyContent[slug || ""];
  usePageTitle(content?.title ?? "Policies");
  if (!content) return <Navigate to="/policies/terms" replace />;
  return <main className="policy-page page-width"><span className="eyebrow">Blessed Headshop Miami</span><h1>{content.title}</h1><p className="policy-intro">{content.intro}</p><div className="policy-sections">{content.sections.map(([title, copy]) => <section key={title}><h2>{title}</h2><p>{copy}</p></section>)}</div><small>Last updated: {slug === "privacy" ? "October 22, 2026" : slug === "shipping" ? "October 14, 2026" : "October 6, 2026"}</small></main>;
}

function NotFound() {
  usePageTitle("Page not found");
  return <main className="not-found page-width"><span>404</span><h1>Nothing to see here.</h1><p>The page may have moved, but the shop is still open.</p><Link className="button button-primary" to="/">Browse products</Link></main>;
}

function AppShell({ children }: { children: ReactNode }) {
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [error, setError] = useState("");
  useEffect(() => { loadCatalog().then(setCatalog).catch((reason) => setError(reason instanceof Error ? reason.message : "Catalog unavailable")); }, []);
  return <CatalogContext.Provider value={{ catalog, error }}><ScrollToTop /><MarketingPageViews /><Header />{children}<SeoMetadata /><Footer /><CartDrawer /><MarketingConsent /><AgeGate /></CatalogContext.Provider>;
}

export default function App() {
  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<Shop />} />
        <Route path="/products" element={<Shop />} />
        <Route path="/shop" element={<Navigate to="/" replace />} />
        <Route path="/shop/raw" element={<Shop brand="RAW" />} />
        <Route path="/shop/elements" element={<Shop brand="ELEMENTS" />} />
        <Route path="/shop/papelito" element={<Shop brand="Papelito" />} />
        <Route path="/shop/puff-life" element={<Shop brand="PUFF Life" />} />
        <Route path="/category/:slug" element={<ShopCategory />} />
        <Route path="/collections/:key" element={<ShopCollection />} />
        <Route path="/product/:handle" element={<ProductDetail />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/blessed-space" element={<BlessedSpace />} />
        <Route path="/venue" element={<Navigate to="/blessed-space" replace />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/contact" element={<Navigate to="/blessed-space#visit" replace />} />
        <Route path="/policies/:slug" element={<Policy />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AppShell>
  );
}
